import React, { useState, useEffect } from 'react';
import { Spin, Checkbox } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  LoadingOutlined, ReloadOutlined, CheckOutlined, StarFilled, PaperClipOutlined,
  ClockCircleOutlined, WarningOutlined, InboxOutlined, FolderOutlined,
} from '@ant-design/icons';
import T from './theme';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import { libellesTraduits } from '../../i18n/libelles';

const G = T.green;
const PAL = ['#5b7c99', '#8e6c88', '#a8744f', '#6b8e6b', '#9c6b6b', '#5f8a8a', '#7a6b9c', '#998b5b', '#5b8e7a', '#9c7a6b'];

const FOLDER_TITLES = libellesTraduits('messagerie:dossiers');

// Table messagerie:natures : OFFRE et PROFORMA_ATTESTATION (valeur de
// TypeTemplateMailEnum sans équivalent dans NatureCourrier) y figurent : elles
// peuvent remonter via le repli sur le modèle (voir categorieMessage.js).
const NATURE_LABEL = libellesTraduits('messagerie:natures');

function avatarColor(s) {
  let h = 0;
  for (const c of String(s || '')) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PAL[h % PAL.length];
}

function ini(n) {
  const p = String(n || '').trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || p[0]?.[1] || '')).toUpperCase();
}

function fDate(d) {
  if (!d) return '';
  const dt = new Date(d); const now = new Date();
  const hier = new Date(); hier.setDate(hier.getDate() - 1);
  if (dt.toDateString() === now.toDateString())
    return dt.toLocaleTimeString(localeIntl(), { hour: '2-digit', minute: '2-digit' });
  if (dt.toDateString() === hier.toDateString()) return i18n.t('messagerie:liste.hier');
  return dt.toLocaleDateString(localeIntl(), { day: '2-digit', month: 'short' });
}

function Tag({ label, bg, fg, mono }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '2px 8px',
      borderRadius: 4, fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
      background: bg, color: fg,
      fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
    }}>
      {label}
    </span>
  );
}

function ConvRow({ conv, active, checked, onToggle, onSelect, onStar, onArchive, onSnooze, onUnsnooze, isRelances, isSnooze }) {
  const { t } = useTranslation('messagerie');
  const p0 = conv.participants[0];
  const col = avatarColor(p0?.name || '');
  const multi = conv.participants.length > 1;

  return (
    <div
      className="cb-row"
      onClick={() => onSelect(conv)}
      style={{
        position: 'relative', display: 'flex', gap: 11,
        padding: '13px 12px 13px 6px', borderBottom: '1px solid #f0f0f0',
        cursor: 'pointer',
        background: (active || checked) ? T.accentSoft : '#fff',
        boxShadow: 'none',
      }}
    >
      {/* Case à cocher de sélection */}
      <div onClick={e => e.stopPropagation()} style={{ flexShrink: 0, alignSelf: 'flex-start', paddingTop: 4 }}>
        <Checkbox checked={!!checked} onChange={() => onToggle(conv.id)} />
      </div>

      {/* Indicateur non-lu */}
      <div style={{ width: 8, flexShrink: 0, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 6 }}>
        {conv.unread && (
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: G, display: 'block' }} />
        )}
      </div>

      {/* Avatar(s) */}
      <div style={{ position: 'relative', width: 40, flexShrink: 0, alignSelf: 'flex-start', marginTop: 2 }}>
        {multi && (
          <div style={{
            position: 'absolute', top: 0, left: 0,
            width: 32, height: 32, borderRadius: '50%',
            background: avatarColor(conv.participants[1]?.name || ''),
            color: '#fff', fontSize: 11, fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '2px solid #fafafa',
          }}>
            {ini(conv.participants[1]?.name)}
          </div>
        )}
        <div style={{
          position: multi ? 'absolute' : 'relative',
          bottom: multi ? 0 : 'auto', right: multi ? 0 : 'auto',
          width: multi ? 30 : 40, height: multi ? 30 : 40,
          borderRadius: '50%', background: col, color: '#fff',
          fontSize: multi ? 11 : 13, fontWeight: 600,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          border: multi ? '2px solid #fff' : 'none',
        }}>
          {ini(p0?.name)}
        </div>
      </div>

      {/* Contenu */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 3 }}>
          <span style={{
            fontSize: 13.5, fontWeight: conv.unread ? 700 : 500,
            color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', flex: 1, minWidth: 0,
          }}>
            {multi
              ? `${p0?.name?.split(' ')[0] || p0?.name} +${conv.participants.length - 1}`
              : p0?.name}
          </span>
          {conv.count > 1 && (
            <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>({conv.count})</span>
          )}
          <span style={{ fontSize: 11, color: '#9ca3af', flexShrink: 0 }}>{fDate(conv.date)}</span>
        </div>

        <div style={{
          fontSize: 13, fontWeight: conv.unread ? 600 : 400,
          color: conv.unread ? '#111827' : '#6b7280',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          marginBottom: 2,
        }}>
          {conv.sujet}
        </div>

        <div style={{
          fontSize: 12, color: '#6b7280',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', marginBottom: 6,
        }}>
          {conv.preview}
        </div>

        {/* Tags */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
          {isRelances && conv.criticality === 'critical' && (
            <Tag label={t('liste.critique', { n: conv.critDays })} bg="#fee2e2" fg="#991b1b" />
          )}
          {isRelances && conv.criticality === 'urgent' && (
            <Tag label={t('liste.urgent', { n: conv.critDays })} bg="#fff7ed" fg="#9a3412" />
          )}
          {isRelances && conv.criticality === 'attention' && (
            <Tag label={t('liste.attention', { n: conv.critDays })} bg="#fefce8" fg="#854d0e" />
          )}
          {conv.nature && (
            <Tag label={NATURE_LABEL[conv.nature] || conv.nature} bg="#dcfce7" fg="#15803d" />
          )}
          {conv.sinistre && (
            <Tag label={<><FolderOutlined style={{ marginRight: 4 }} />{conv.sinistre}</>} bg="#dbeafe" fg="#1e40af" mono />
          )}
          {conv.attachments > 0 && (
            <Tag label={<><PaperClipOutlined /> {conv.attachments}</>} bg="#f3f4f6" fg="#6b7280" />
          )}
          {conv.starred && <Tag label={<StarFilled />} bg="#fffbeb" fg="#a16207" />}
          {conv.snoozeLabel && <Tag label={<><ClockCircleOutlined /> {conv.snoozeLabel}</>} bg="#f5f3ff" fg="#6d28d9" />}
          {conv.statutEnvoi === 'ECHEC' && <Tag label={<><WarningOutlined /> {t('liste.echecEnvoi')}</>} bg="#fee2e2" fg="#dc2626" />}
          {conv.draft && <Tag label={t('liste.brouillon')} bg="#f3f4f6" fg="#6b7280" />}
        </div>
      </div>

      {/* Actions en survol */}
      <div className="cb-actions" style={{
        position: 'absolute', right: 8, top: 10,
        display: 'flex', gap: 3, opacity: 0, transition: 'opacity .12s',
      }}>
        <ActionBtn icon={<StarFilled />} title={conv.starred ? t('liste.retirerSuivis') : t('liste.suivre')} onClick={e => onStar(conv, e)} />
        {isSnooze ? (
          <ActionBtn icon={<InboxOutlined />} title={t('liste.remettreBoite')} onClick={e => onUnsnooze(conv, e)} />
        ) : (
          <SnoozeBtn onSnooze={(delai, e) => onSnooze(conv, delai, e)} />
        )}
        <ActionBtn icon={<CheckOutlined />} title={t('liste.archiver')} onClick={e => onArchive(conv, e)} />
      </div>
    </div>
  );
}

/* Bouton « Reporter » : trois échéances usuelles, sans sélecteur de date. */
function SnoozeBtn({ onSnooze }) {
  const { t } = useTranslation('messagerie');
  const [open, setOpen] = useState(false);
  const CHOIX = [
    { key: '1h', label: t('liste.reporter.uneHeure') },
    { key: 'demain', label: t('liste.reporter.demain') },
    { key: 'semaine', label: t('liste.reporter.semaine') },
  ];
  return (
    <span style={{ position: 'relative' }} onMouseLeave={() => setOpen(false)}>
      <ActionBtn icon={<ClockCircleOutlined />} title={t('liste.reporter.titre')} onClick={e => { e.stopPropagation(); setOpen(o => !o); }} />
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 28, zIndex: 20,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 6px 20px rgba(0,0,0,.12)', padding: 4, minWidth: 170,
        }}>
          {CHOIX.map(c => (
            <button
              key={c.key}
              onClick={e => { e.stopPropagation(); setOpen(false); onSnooze(c.key, e); }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', border: 'none',
                background: 'none', padding: '7px 10px', fontSize: 12.5,
                cursor: 'pointer', borderRadius: 6, color: '#374151',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

function ActionBtn({ icon, title, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick} title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        width: 26, height: 26, border: 'none', cursor: 'pointer', fontSize: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: hov ? '#f0f0f0' : '#fff',
        borderRadius: 6, boxShadow: '0 1px 2px rgba(0,0,0,.12)',
      }}
    >
      {icon}
    </button>
  );
}

function IconBtn({ icon, title, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick} title={title}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        border: 'none', background: hov ? '#f0f0f0' : 'transparent',
        width: 30, height: 30, borderRadius: 6, cursor: 'pointer',
        color: '#6b7280', fontSize: 14,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {icon}
    </button>
  );
}

function TextBtn({ children, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        border: 'none', background: hov ? '#f0f0f0' : 'transparent',
        padding: '5px 9px', borderRadius: 6, cursor: 'pointer',
        color: '#6b7280', fontSize: 12.5, fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}

export default function ListeConversations({
  folder, convs, loading, activeConvId, onSelect, onMarkAllRead, onRefresh, onStar, onArchive,
  onSnooze, onUnsnooze, hasMore = false, loadingMore = false, onLoadMore, totalMessages = 0,
}) {
  const { t } = useTranslation('messagerie');
  const isRelances = folder === 'relances';
  const isSnooze = folder === 'snooze';
  const unreadCount = convs.filter(c => c.unread).length;
  const critCount = convs.filter(c => c.criticality).length;

  // Sélection multiple par cases à cocher
  const [selected, setSelected] = useState(() => new Set());
  useEffect(() => { setSelected(new Set()); }, [folder]); // réinit au changement de dossier

  // Tri par criticité dans le dossier relances
  const sortedConvs = isRelances
    ? [...convs].sort((a, b) => {
      const order = { critical: 0, urgent: 1, attention: 2 };
      return (order[a.criticality] ?? 3) - (order[b.criticality] ?? 3);
    })
    : convs;

  const visibleIds = sortedConvs.map(c => c.id);
  const allChecked = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someChecked = visibleIds.some(id => selected.has(id)) && !allChecked;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(visibleIds));
  const toggleOne = id => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const selectedConvs = () => sortedConvs.filter(c => selected.has(c.id));

  /* Défilement infini : on demande la tranche suivante avant d'atteindre le bas,
     pour que le chargement soit déjà en cours quand l'utilisateur y arrive. Le
     bouton en pied de liste reste le repli explicite (et le seul recours si le
     chargement précédent a échoué). */
  const onScroll = e => {
    if (!hasMore || loadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 240) onLoadMore?.();
  };
  const bulkArchive = () => { selectedConvs().forEach(c => onArchive?.(c)); setSelected(new Set()); };
  const bulkStar = () => { selectedConvs().forEach(c => onStar?.(c)); setSelected(new Set()); };

  return (
    <section style={{
      width: 380, flexShrink: 0, background: T.panel, fontFamily: T.font,
      borderRight: `1px solid ${T.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
    }}>
      {/* En-tête */}
      <div style={{ padding: '14px 16px 10px', borderBottom: '1px solid #ececec', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>
            {FOLDER_TITLES[folder] || folder}
          </div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>
            {unreadCount > 0
              ? t('liste.nonLus', { count: unreadCount })
              /* Sur un dossier paginé, afficher le seul nombre chargé laissait croire
                 que la boîte s'arrêtait là : on montre le total annoncé par le serveur. */
              : totalMessages > convs.length
                ? t('liste.chargesSurTotal', { charges: convs.length, total: totalMessages })
                : t('liste.conversations', { count: convs.length })}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Checkbox
            checked={allChecked}
            indeterminate={someChecked}
            onChange={toggleAll}
            title={t('liste.toutSelectionner')}
          />
          {selected.size > 0 ? (
            <>
              <span style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                {t('liste.selectionnes', { count: selected.size })}
              </span>
              <div style={{ flex: 1 }} />
              <IconBtn icon={<StarFilled />} title={t('liste.suivreSelection')} onClick={bulkStar} />
              <IconBtn icon={<InboxOutlined />} title={t('liste.archiverSelection')} onClick={bulkArchive} />
              <TextBtn onClick={() => setSelected(new Set())}>{t('liste.annuler')}</TextBtn>
            </>
          ) : (
            <>
              <IconBtn icon={<ReloadOutlined />} title={t('liste.rafraichir')} onClick={onRefresh} />
              <IconBtn icon={<CheckOutlined />} title={t('liste.toutMarquerLu')} onClick={onMarkAllRead} />
              <div style={{ flex: 1 }} />
              <TextBtn>{t('liste.trier')}</TextBtn>
              <TextBtn>{t('liste.filtrer')}</TextBtn>
            </>
          )}
        </div>
      </div>

      {/* Bannière urgences relances */}
      {isRelances && critCount > 0 && (
        <div style={{
          background: '#fee2e2', color: '#991b1b', fontSize: 12.5, fontWeight: 500,
          padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'flex-start',
          lineHeight: 1.4, flexShrink: 0,
        }}>
          <WarningOutlined />
          <span>
            {t('liste.banniereRelances', { count: critCount })}
          </span>
        </div>
      )}

      {/* Skeleton loading */}
      {loading && (
        <div style={{ padding: '8px 0', flexShrink: 0 }}>
          {[1, 2, 3, 4].map(n => (
            <div key={n} style={{ height: 84, padding: '14px 16px', display: 'flex', gap: 12, borderBottom: '1px solid #f0f0f0' }}>
              <div className="cb-skel" style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, alignSelf: 'center' }} />
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                <div className="cb-skel" style={{ height: 11, width: '60%' }} />
                <div className="cb-skel" style={{ height: 9, width: '85%' }} />
                <div className="cb-skel" style={{ height: 9, width: '40%' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && convs.length === 0 && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: 40, textAlign: 'center',
        }}>
          <div style={{ marginBottom: 14 }}><InboxOutlined style={{ fontSize: 40, color: '#cbd5e1' }} /></div>
          <div style={{ fontSize: 14, fontWeight: 600, color: '#6b7280' }}>{t('liste.aucune')}</div>
          <div style={{ fontSize: 12.5, marginTop: 6, color: '#9ca3af', maxWidth: 200, lineHeight: 1.45 }}>
            {folder === 'trash'
              ? t('liste.corbeilleVide')
              : t('liste.vueVide')}
          </div>
        </div>
      )}

      {/* Loading spinner centré (si loading + pas encore de résultats) */}
      {loading && convs.length === 0 && (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: G }} spin />} />
        </div>
      )}

      {/* Liste */}
      {!loading && sortedConvs.length > 0 && (
        <div className="cb-scroll" style={{ flex: 1, overflowY: 'auto' }} onScroll={onScroll}>
          {sortedConvs.map(conv => (
            <ConvRow
              key={conv.id}
              conv={conv}
              active={conv.id === activeConvId}
              checked={selected.has(conv.id)}
              onToggle={toggleOne}
              onSelect={onSelect}
              onStar={onStar}
              onArchive={onArchive}
              onSnooze={onSnooze}
              onUnsnooze={onUnsnooze}
              isRelances={isRelances}
              isSnooze={isSnooze}
            />
          ))}

          {/* Pied de liste : progression et chargement de la suite */}
          {hasMore && (
            <div style={{ padding: '14px 16px 20px', textAlign: 'center' }}>
              <button
                onClick={() => onLoadMore?.()}
                disabled={loadingMore}
                style={{
                  border: `1px solid ${T.border}`, background: '#fff', borderRadius: 8,
                  padding: '8px 18px', fontSize: 12.5, fontWeight: 600, color: '#374151',
                  cursor: loadingMore ? 'default' : 'pointer', fontFamily: 'inherit',
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                }}
              >
                {loadingMore
                  ? <><Spin indicator={<LoadingOutlined style={{ fontSize: 13, color: G }} spin />} /> {t('liste.chargement')}</>
                  : t('liste.chargerPlus')}
              </button>
              {totalMessages > 0 && (
                <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 8 }}>
                  {t('liste.surTotal', { n: sortedConvs.length, total: totalMessages })}
                </div>
              )}
            </div>
          )}

          {!hasMore && totalMessages > 0 && sortedConvs.length >= totalMessages && (
            <div style={{
              padding: '14px 16px 20px', textAlign: 'center',
              fontSize: 11.5, color: '#9ca3af',
            }}>
              {t('liste.tousCharges', { total: totalMessages })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
